import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, TrendingUp, Percent } from "lucide-react";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState("");

  useEffect(() => { api.get("/config").then((r) => setPlans(r.data.plans)).catch(() => {}); }, []);

  const subscribe = async (planKey) => {
    if (!user) { toast.error("Please log in as a provider"); navigate("/signup?role=provider"); return; }
    if (user.role !== "provider") { toast.error("Only providers can subscribe"); return; }
    if (planKey === "free") { toast.success("You're on the Free plan!"); navigate("/provider"); return; }
    setLoading(planKey);
    try {
      const co = await api.post("/payments/checkout", { kind: "subscription", plan_key: planKey, origin_url: window.location.origin });
      window.location.href = co.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setLoading("");
    }
  };

  const order = ["free", "standard", "premium"];

  return (
    <Layout>
      <div className="hero-gradient grain text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold"><Sparkles className="w-3.5 h-3.5 text-gold" /> For Landscapers</span>
          <h1 className="font-display text-5xl font-extrabold mt-5">Plans that grow with your business</h1>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Choose a monthly subscription for premium visibility, or start free and pay only a {" "}15% commission on completed jobs.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-10 grid md:grid-cols-3 gap-6">
        {order.map((key) => {
          const plan = plans[key];
          if (!plan) return null;
          const featured = key === "premium";
          return (
            <div key={key} data-testid={`plan-${key}`} className={`rounded-2xl p-8 border-2 flex flex-col ${featured ? "border-gold bg-white shadow-2xl scale-[1.02]" : "border-border bg-white"}`}>
              {featured && <span className="self-start bg-gold text-white text-xs font-bold px-3 py-1 rounded-full mb-3">MOST POPULAR</span>}
              <h3 className="font-display text-2xl font-extrabold text-lawn-ink">{plan.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-lawn-ink">${plan.price}</span>
                {plan.price > 0 && <span className="text-muted-foreground mb-1">/mo</span>}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-lawn-ink/80"><Check className="w-4 h-4 text-lawn mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Button data-testid={`subscribe-${key}`} onClick={() => subscribe(key)} disabled={loading === key}
                className={`mt-6 rounded-full font-semibold h-11 ${featured ? "bg-gold hover:bg-gold-dark text-white" : "bg-lawn hover:bg-lawn-dark"}`}>
                {loading === key ? "Redirecting…" : plan.price === 0 ? "Start Free" : "Upgrade Now"}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-6">
        <div className="bg-lawn-light rounded-2xl p-8">
          <Percent className="w-8 h-8 text-lawn-dark mb-3" />
          <h3 className="font-display text-2xl font-bold text-lawn-ink">Prefer commission?</h3>
          <p className="text-lawn-ink/70 mt-2">Skip the monthly fee. Pay just 15% on completed jobs booked through the platform. Perfect for solo operators getting started.</p>
        </div>
        <div className="bg-gold-light rounded-2xl p-8">
          <TrendingUp className="w-8 h-8 text-gold-dark mb-3" />
          <h3 className="font-display text-2xl font-bold text-lawn-ink">Boost your visibility</h3>
          <p className="text-lawn-ink/70 mt-2">Add sponsored placement ($49) or a Featured Provider boost ($99) to jump to the top of search results for 30 days.</p>
        </div>
      </div>
    </Layout>
  );
}
